import React from "react";
import ReactPlayer from "react-player";
import { motion } from "framer-motion";
import { fadeIn } from "../../utils/variants";
import { transition } from "../../utils/transition";

const knowledgeImages = [
  "/new_img/know_1.png",
  "/new_img/know_2.png",
  "/new_img/know_3.png",
  "/new_img/know_4.png",
  "/new_img/know_5.png",
  "/new_img/know_6.png",
  "/new_img/know_7.png",
  "/new_img/know_8.png",
  "/new_img/know_9.png",
  "/new_img/know_10.png",
];

const KnowledgeBank = () => {
  return (
    <div className="relative min-h-screen bg-[#020617] text-slate-50">
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,#0b1220_1px,transparent_1px),linear-gradient(to_bottom,#0b1220_1px,transparent_1px)] [background-size:46px_46px]" />
      <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-[#00ad0f]/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#2563eb]/30 blur-3xl" />

      {/* HERO – Knowledge Command Deck */}
      <section className="relative pt-28 md:pt-32 lg:pt-36 border-b border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-5 pb-10 md:pb-14 md:flex-row md:items-center">
          {/* Text side */}
          <motion.div
            variants={fadeIn("Up")}
            transition={transition()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative flex-1 rounded-3xl border border-white/10 bg-[#020819]/90 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.7)] backdrop-blur-xl md:p-8"
          >
            {/* accent bar */}
            <div className="absolute inset-x-8 top-0 h-[3px] rounded-b-full bg-gradient-to-r from-[#00ad0f] via-white/40 to-[#062b6c]" />

            <p className="mb-2 text-[11px] uppercase tracking-[0.32em] text-slate-400">
              Knowledge Bank
            </p>

            <h1 className="mb-3 text-3xl font-semibold uppercase text-slate-50 md:text-4xl lg:text-[2.6rem] lg:leading-tight">
              <span className="inline-block bg-gradient-to-r from-[#00ad0f] to-[#7fffb0] bg-clip-text text-transparent">
                Engineering Intelligence Hub
              </span>
            </h1>

            <p className="max-w-xl text-sm leading-relaxed text-slate-200 md:text-[0.98rem]">
              The Scaftech Knowledge Bank is your access point to curated
              technical insights, safety briefings, lifting standards, and
              training content—built to support engineers, HSE professionals,
              and operations teams across oil &amp; gas, marine, construction,
              and industrial projects.
            </p>

            <div className="mt-6 grid gap-3 text-[11px] md:grid-cols-3">
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-3">
                <p className="uppercase tracking-[0.22em] text-emerald-300">
                  Video Briefings
                </p>
                <p className="mt-1 text-xs text-emerald-100">
                  Bite-sized sessions from field experts and trainers.
                </p>
              </div>
              <div className="rounded-2xl border border-sky-400/30 bg-sky-400/5 px-3 py-3">
                <p className="uppercase tracking-[0.22em] text-sky-300">
                  Standards &amp; Compliance
                </p>
                <p className="mt-1 text-xs text-sky-100">
                  References aligned with global codes and regulations.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-500/40 bg-slate-800/60 px-3 py-3">
                <p className="uppercase tracking-[0.22em] text-slate-300">
                  Continuous Learning
                </p>
                <p className="mt-1 text-xs text-slate-200">
                  Built to support upskilling across all experience levels.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Image / visual side */}
          <motion.div
            variants={fadeIn("Up")}
            transition={transition()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative flex-1"
          >
            <div className="relative h-56 overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 shadow-[0_24px_80px_rgba(0,0,0,0.8)] md:h-72">
              <img
                src="/new_img/crane-2.jpg"
                alt="Crane operations knowledge capture"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,rgba(15,23,42,0.9)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.9)_1px,transparent_1px)] [background-size:32px_32px]" />

              <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between text-[11px] text-slate-100">
                <div className="max-w-xs truncate">
                  <span className="block text-[10px] uppercase tracking-[0.26em] text-slate-400">
                    Live Knowledge Capture
                  </span>
                  <span className="truncate text-sm">
                    Lifting operations • Asset integrity • HSE
                  </span>
                </div>
                <span className="text-[9px] text-slate-300">
                  Updated • On Demand
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED VIDEO + DESCRIPTION */}
      <motion.section
        variants={fadeIn("Up")}
        transition={transition()}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        className="relative py-10 md:py-16"
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-5 xl:flex-row">
          {/* Featured video */}
          <div className="w-full rounded-3xl border border-slate-700/60 bg-black/60 p-3 shadow-[0_18px_60px_rgba(0,0,0,0.8)]">
            <div className="aspect-video overflow-hidden rounded-2xl">
              <ReactPlayer
                url="https://www.youtube.com/watch?v=E3odTnNeSA8"
                width="100%"
                height="100%"
              />
            </div>
          </div>

          {/* Description / CTA */}
          <div className="flex w-full flex-col gap-4 rounded-3xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-[0_18px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl md:p-8">
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Featured Session
            </p>

            <h2 className="text-2xl font-bold uppercase md:text-3xl">
              Operational Excellence &amp; Certification Pathways
            </h2>

            <p className="text-sm leading-relaxed text-slate-200 md:text-[0.98rem]">
              Scaftech provides world-class specialist Asset Integrity Assurance
              and Training services throughout the asset life-cycle. We service
              a wide range of markets including Oil &amp; Gas, Marine,
              Construction, and Real Estate. Through structured training and
              certified programs, we equip teams with the practical,
              standards-driven knowledge required for safe, reliable, and
              compliant operations.
            </p>

            <p className="text-sm leading-relaxed text-slate-200 md:text-[0.98rem]">
              This Knowledge Bank connects our field experience, certifications,
              and training modules—giving learners a single place to explore
              video briefings, technical guides, and pathway information for
              professional development.
            </p>

            <div className="mt-2 self-start">
              <a
                href="/certification"
                className="group relative inline-flex h-11 items-center justify-center overflow-hidden rounded-full bg-[#082b6c] px-7 text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-200 duration-500 hover:bg-[#2b9768]"
              >
                <div className="relative inline-flex items-center transition group-hover:-translate-x-4">
                  <div className="absolute -translate-y-0.5 translate-x-0 opacity-100 transition group-hover:-translate-x-4 group-hover:opacity-0">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                    >
                      <path
                        d="M7.22303 0.665992C7.32551 0.419604 7.67454 0.419604 7.77702 0.665992L9.41343 4.60039C9.45663 4.70426 9.55432 4.77523 9.66645 4.78422L13.914 5.12475C14.18 5.14607 14.2878 5.47802 14.0852 5.65162L10.849 8.42374C10.7636 8.49692 10.7263 8.61176 10.7524 8.72118L11.7411 12.866C11.803 13.1256 11.5206 13.3308 11.2929 13.1917L7.6564 10.9705C7.5604 10.9119 7.43965 10.9119 7.34365 10.9705L3.70718 13.1917C3.47945 13.3308 3.19708 13.1256 3.25899 12.866L4.24769 8.72118C4.2738 8.61176 4.23648 8.49692 4.15105 8.42374L0.914889 5.65162C0.712228 5.47802 0.820086 5.14607 1.08608 5.12475L5.3336 4.78422C5.44573 4.77523 5.54342 4.70426 5.58662 4.60039L7.22303 0.665992Z"
                        fill="currentColor"
                      ></path>
                    </svg>
                  </div>
                  <span className="pl-5">
                    View Accreditation &amp; Awards
                  </span>
                  <div className="absolute right-0 translate-x-10 opacity-0 transition group-hover:translate-x-4 group-hover:opacity-100">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                    >
                      <path
                        d="M8.14645 3.14645C8.34171 2.95118 8.65829 2.95118 8.85355 3.14645L12.8536 7.14645C13.0488 7.34171 13.0488 7.65829 12.8536 7.85355L8.85355 11.8536C8.65829 12.0488 8.34171 12.0488 8.14645 11.8536C7.95118 11.6583 7.95118 11.3417 8.14645 11.1464L11.2929 8H2.5C2.22386 8 2 7.77614 2 7.5C2 7.22386 2.22386 7 2.5 7H11.2929L8.14645 3.85355C7.95118 3.65829 7.95118 3.34171 8.14645 3.14645Z"
                        fill="currentColor"
                        fillRule="evenodd"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </motion.section>

      {/* RESOURCE IMAGE LIBRARY */}
      <motion.section
        variants={fadeIn("Up")}
        transition={transition()}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        className="relative pb-10 md:pb-16"
      >
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                Resource Library
              </p>
              <h3 className="text-lg font-semibold text-slate-50 md:text-xl">
                Visual guides and quick-reference sheets.
              </h3>
              <p className="mt-1 text-sm text-slate-300 max-w-xl">
                Swipe through lifting charts, safety prompts, and process
                snapshots designed for quick on-site reference and toolbox
                talks.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {knowledgeImages.map((src, idx) => (
              <div
                key={src}
                className="group overflow-hidden rounded-2xl border border-slate-700/70 bg-slate-900/70 shadow-[0_14px_40px_rgba(0,0,0,0.7)]"
              >
                <div className="overflow-hidden">
                  <img
                    src={src}
                    alt={`Knowledge asset ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* KNOWLEDGE CATEGORIES STRIP */}
      <motion.section
        variants={fadeIn("Up")}
        transition={transition()}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        className="relative pb-14"
      >
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                Knowledge Zones
              </p>
              <h3 className="text-lg font-semibold text-slate-50 md:text-xl">
                Explore key domains within the Scaftech Knowledge Bank.
              </h3>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex flex-col gap-2 rounded-2xl border border-slate-700/60 bg-slate-900/70 p-4 shadow-[0_14px_40px_rgba(0,0,0,0.7)]">
              <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-300">
                Asset Integrity
              </p>
              <p className="text-sm text-slate-200">
                Guides, case studies, and field learnings on inspection,
                structural integrity, and lifecycle performance of critical
                assets.
              </p>
            </div>

            <div className="flex flex-col gap-2 rounded-2xl border border-slate-700/60 bg-slate-900/70 p-4 shadow-[0_14px_40px_rgba(0,0,0,0.7)]">
              <p className="text-[11px] uppercase tracking-[0.24em] text-sky-300">
                HSE &amp; Compliance
              </p>
              <p className="text-sm text-slate-200">
                Content focused on safety culture, regulatory frameworks, and
                practical HSE implementation in high-risk environments.
              </p>
            </div>

            <div className="flex flex-col gap-2 rounded-2xl border border-slate-700/60 bg-slate-900/70 p-4 shadow-[0_14px_40px_rgba(0,0,0,0.7)]">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#7fffb0]">
                Lifting &amp; Training
              </p>
              <p className="text-sm text-slate-200">
                Lifting plans, rigging best practices, and training highlights
                to support competent lifting operations and certification paths.
              </p>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default KnowledgeBank;
